import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Trophy, Phone, Mail, Upload, FileText, FileCheck2 } from "lucide-react";
import { formatPhoneNumber } from "@/lib/formatPhone";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { displayCompanyName } from "@/lib/displayCompanyName";

interface BrokerContact {
  company_name: string;
  contact_name: string;
  email: string | null;
  phone: string | null;
}

/**
 * Shipper-only post-award card shown on LRDirectOpportunityPage once the load is awarded.
 * Surfaces the winning broker's contact info and stubs for Rate Confirmation / POD upload.
 */
export function ShipperPostAwardCard({ loadId }: { loadId: string }) {
  const [contact, setContact] = useState<BrokerContact | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const { data } = await supabase.rpc("get_direct_award_broker_contact", {
        p_load_id: loadId,
      } as any);
      if (cancelled) return;
      const row = (data as any[])?.[0] as BrokerContact | undefined;
      setContact(row ?? null);
      setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [loadId]);

  return (
    <div className="border-2 border-brand-teal/30 rounded-lg bg-brand-teal/5 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Trophy className="h-5 w-5 text-brand-teal" />
        <h2 className="text-[10px] font-bold text-brand-teal uppercase tracking-wider">
          Post-Award Workspace
        </h2>
      </div>

      {/* Broker contact */}
      <div>
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
          Winning Broker
        </p>
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading contact…</div>
        ) : !contact ? (
          <div className="text-xs text-muted-foreground">Contact details unavailable.</div>
        ) : (
          <div className="space-y-1.5 text-sm">
            <p className="font-semibold text-foreground">{displayCompanyName(contact.company_name)}</p>
            <p className="text-foreground">{contact.contact_name}</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {contact.phone && (
                <Button
                  size="sm"
                  className="gap-1.5 text-xs"
                  style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                  asChild
                >
                  <a href={`tel:${contact.phone}`}>
                    <Phone className="h-3.5 w-3.5" />
                    Call {formatPhoneNumber(contact.phone)}
                  </a>
                </Button>
              )}
              {contact.email && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs"
                  asChild
                >
                  <a href={`mailto:${contact.email}`}>
                    <Mail className="h-3.5 w-3.5" />
                    {contact.email}
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
          Documents
        </p>
        <TooltipProvider>
          <div className="space-y-2">
            <DocStubRow icon={<FileText className="h-4 w-4 text-muted-foreground" />} label="Rate Confirmation" />
            <DocStubRow icon={<FileCheck2 className="h-4 w-4 text-muted-foreground" />} label="Proof of Delivery (POD)" />
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
}

function DocStubRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
      <div className="flex items-center gap-2 text-sm text-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>
            <Button size="sm" variant="outline" className="gap-1.5 text-xs" disabled>
              <Upload className="h-3.5 w-3.5" />
              Upload
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Coming soon</TooltipContent>
      </Tooltip>
    </div>
  );
}
